/**
 * The table used by every list screen.
 *
 * Columns are described rather than written as JSX so the same definition can
 * drive the on-screen table and an export. `align: 'right'` on a money column
 * also switches on tabular figures, which is what makes a column of amounts
 * scannable.
 *
 *   <DataTable
 *       columns={[
 *           { key: 'Order_Number', header: 'Order #' },
 *           { key: 'Total_Payable', header: 'Total', align: 'right',
 *             render: (row) => money(row.Total_Payable) }
 *       ]}
 *       rows={orders}
 *       onRowClick={openOrder}
 *   />
 *
 * Below `md` it stops being a table.
 *
 * These screens run five to nine columns. On a phone that is a table nobody can
 * read: either the columns squeeze until every cell wraps to four lines, or the
 * whole thing scrolls sideways and the row you are reading loses the name it
 * belongs to. So under `md` each row is drawn as a card instead - the first
 * column as its heading, the rest as labelled lines beneath. Same column
 * definitions, same data, same click target; only the arrangement differs, and
 * nothing has to be duplicated at the call site.
 *
 * Per-column controls for that view:
 *
 *   mobileHidden  leave this column out of the card. For a column that is
 *                 noise without its neighbours - an ID, a repeated code.
 *   primary       use this column as the card's heading instead of the first.
 *   mobileLabel   a shorter heading than the table's, where the table's is a
 *                 phrase that only reads well above a column.
 */

import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { SpinnerBlock } from './Spinner';
import { EmptyState } from './Feedback';
import { useBreakpoint } from '../../hooks/useMediaQuery';

/* ------------------------------- one card --------------------------------- */

const RowCard = ({ row, index, columns, onRowClick, rowClassName }) => {
    const cell = (column) => (column.render ? column.render(row, index) : row[column.key]);

    // The heading: whichever column asked to be it, else the first one that is
    // shown at all.
    const visible = columns.filter((column) => !column.mobileHidden);
    const head = visible.find((column) => column.primary) || visible[0];
    const rest = visible.filter((column) => column !== head);

    return (
        <div
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            role={onRowClick ? 'button' : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            onKeyDown={onRowClick
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onRowClick(row);
                    }
                }
                : undefined}
            className={`row-card ${onRowClick ? 'cursor-pointer active:bg-slate-50' : ''}
                        ${rowClassName?.(row) || ''}`}
        >
            {head && (
                <div className="mb-1.5 text-sm font-semibold text-slate-800">
                    {cell(head)}
                </div>
            )}

            {rest.map((column) => (
                <div key={column.key} className="row-card-line">
                    <span className="row-card-label">
                        {column.mobileLabel ?? column.header}
                    </span>
                    <span className={`row-card-value ${column.align === 'right' ? 'tabular' : ''}`}>
                        {cell(column)}
                    </span>
                </div>
            ))}
        </div>
    );
};

/* ------------------------------ the component ----------------------------- */

const DataTable = ({
    columns,
    rows,
    loading = false,
    emptyTitle = 'Nothing to show',
    emptyDescription = null,
    emptyIcon = undefined,
    onRowClick = null,
    rowKey = (row, index) => row.id ?? index,
    // Optional: a row can be tinted, e.g. a voided order shown in red
    rowClassName = null,
    meta = null,
    onPageChange = null,
    footer = null,
    // A short table - two or three columns - reads fine on a phone as a table,
    // so a caller can keep one.
    stackOnMobile = true,
    className = ''
}) => {
    const { isMobile } = useBreakpoint();
    const stacked = stackOnMobile && isMobile;

    if (loading) {
        return (
            <div className={`table-wrap ${className}`}>
                <SpinnerBlock />
            </div>
        );
    }

    if (!rows?.length) {
        return (
            <div className={`table-wrap ${className}`}>
                <EmptyState title={emptyTitle} description={emptyDescription} icon={emptyIcon} />
            </div>
        );
    }

    /* Pagination is the same either way. */
    const pager = meta && meta.totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-slate-600 sm:text-sm">
            <span className="tabular">
                {(meta.page - 1) * meta.pageSize + 1}
                –{Math.min(meta.page * meta.pageSize, meta.total)} of {meta.total}
            </span>

            <div className="flex items-center gap-1">
                <button
                    type="button"
                    className="btn-ghost btn-sm"
                    disabled={meta.page <= 1}
                    onClick={() => onPageChange?.(meta.page - 1)}
                >
                    <FiChevronLeft />
                    <span className="hidden xs:inline">Previous</span>
                </button>
                <span className="tabular px-1 sm:px-2">{meta.page} / {meta.totalPages}</span>
                <button
                    type="button"
                    className="btn-ghost btn-sm"
                    disabled={!meta.hasNext}
                    onClick={() => onPageChange?.(meta.page + 1)}
                >
                    <span className="hidden xs:inline">Next</span>
                    <FiChevronRight />
                </button>
            </div>
        </div>
    );

    if (stacked) {
        return (
            <div className={`flex min-h-0 flex-col gap-2 ${className}`}>
                <div className="min-h-0 flex-1 overflow-y-auto">
                    {rows.map((row, index) => (
                        <RowCard
                            key={rowKey(row, index)}
                            row={row}
                            index={index}
                            columns={columns}
                            onRowClick={onRowClick}
                            rowClassName={rowClassName}
                        />
                    ))}

                    {/* A totals row is not a row of data, so it does not become
                        a card - it keeps its table shape inside one last
                        panel, where the figures still line up. */}
                    {footer && (
                        <div className="row-card mt-2 overflow-x-auto bg-slate-50">
                            <table className="table">
                                <tfoot className="font-medium">{footer}</tfoot>
                            </table>
                        </div>
                    )}
                </div>

                {pager}
            </div>
        );
    }

    return (
        <div className={`flex min-h-0 flex-col gap-2 ${className}`}>
            <div className="table-wrap min-h-0 flex-1">
                <table className="table">
                    <thead>
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    className={column.align === 'right' ? 'text-right' : ''}
                                    style={{
                                        background: '#808080',
                                        color: '#ffffff',

                                        ...(column.width ? { width: column.width } : {})
                                    }}
                                >
                                    {column.header}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {rows.map((row, index) => (
                            <tr
                                key={rowKey(row, index)}
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                                className={`${onRowClick ? 'cursor-pointer' : ''} ${rowClassName?.(row) || ''}`}
                            >
                                {columns.map((column) => (
                                    <td
                                        key={column.key}
                                        className={column.align === 'right' ? 'tabular text-right' : ''}
                                    >
                                        {column.render ? column.render(row, index) : row[column.key]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>

                    {footer && <tfoot className="bg-slate-50 font-medium">{footer}</tfoot>}
                </table>
            </div>

            {pager}
        </div>
    );
};

export default DataTable;
